import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function LEDLightingPage() {
  useEffect(() => {
    document.title = 'LED Lighting Upgrades Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Energy-efficient LED lighting upgrades in Watford. Save money with professional LED installation. Call 020 3198 1818 for a free quote.');
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "telephone": "020 3198 1818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "url": "https://beyondelectrical.co.uk/services/installations/led-lighting",
      "areaServed": "Watford",
      "serviceType": "LED Lighting Upgrades",
      "openingHours": "Mo-Su 00:00-23:59"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/3e05d5e1afc37ca0a37147d6b5e7ca94.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-leaf-fill"></i>
              Energy Efficient
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Watford LED Specialists
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            LED Lighting Upgrades<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Upgrade to energy-efficient LED lighting and save up to 80% on your electricity bills. Professional LED installation services for homes and businesses throughout Watford and Rickmansworth.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Our LED Lighting Upgrade Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complete LED conversion and installation for Watford properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  💡
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">LED Downlight Installation</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Replace old halogen downlights with energy-efficient LED downlights throughout your Watford home, reducing energy costs by up to 80%.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🌿
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">LED Bulb Conversion</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Convert existing light fittings to LED-compatible fixtures, upgrading all bulbs to long-lasting LED alternatives in Watford properties.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🎨
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">LED Strip Lighting</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Install LED strip lights for under-cabinet lighting, cove lighting, and accent lighting in kitchens and living spaces throughout Watford.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🏢
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Commercial LED Upgrades</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    LED lighting upgrades for offices, shops, and commercial premises in Watford, reducing running costs and improving illumination.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🌙
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Dimmable LED Systems</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Install dimmable LED lighting with compatible dimmer switches for adjustable ambiance in your Watford home.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🤖
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Smart LED Lighting</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Install smart LED bulbs and systems with app control, voice control, and automation for modern Watford homes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Benefits of LED Lighting Upgrades
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Why Watford homeowners are switching to LED lighting
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                💰
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Save Up to 80%</h3>
              <p className="text-gray-600 leading-relaxed">
                LED lights use up to 80% less energy than traditional bulbs, dramatically reducing electricity bills for Watford homeowners.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                ⏰
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Long Lifespan</h3>
              <p className="text-gray-600 leading-relaxed">
                LED bulbs last 25,000-50,000 hours compared to 1,000 hours for traditional bulbs, reducing replacement costs in Watford homes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🌿
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Eco-Friendly</h3>
              <p className="text-gray-600 leading-relaxed">
                Reduce your carbon footprint with energy-efficient LED lighting that's better for the environment in Watford.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                ✨
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Better Light Quality</h3>
              <p className="text-gray-600 leading-relaxed">
                Modern LED lights provide instant, flicker-free illumination with excellent color rendering for Watford properties.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🔥
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Cooler Operation</h3>
              <p className="text-gray-600 leading-relaxed">
                LED lights produce minimal heat compared to halogen bulbs, reducing fire risk and cooling costs in Watford homes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🎨
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Design Flexibility</h3>
              <p className="text-gray-600 leading-relaxed">
                LED technology enables slim, modern lighting designs and color-changing options for creative Watford interiors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Savings Calculator */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#0A1628] to-[#0F2847] rounded-3xl p-8 md:p-12 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              LED Lighting Savings for Watford Homes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="text-[#FFD700] text-4xl font-bold mb-2">80%</div>
                <p className="text-white text-sm">Energy Savings</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="text-[#FFD700] text-4xl font-bold mb-2">25x</div>
                <p className="text-white text-sm">Longer Lifespan</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="text-[#FFD700] text-4xl font-bold mb-2">£200+</div>
                <p className="text-white text-sm">Annual Savings</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm">
              Average Watford household with 20 lights switching from halogen to LED
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Watford's LED lighting upgrade specialists
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🏆</div>
              <h3 className="text-xl font-bold text-white mb-2">15 Years Experience</h3>
              <p className="text-gray-300 text-sm">
                LED experts since 2009
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="text-xl font-bold text-white mb-2">Quality LEDs</h3>
              <p className="text-gray-300 text-sm">
                Premium brands only
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💡</div>
              <h3 className="text-xl font-bold text-white mb-2">Expert Advice</h3>
              <p className="text-gray-300 text-sm">
                Free lighting consultation
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💯</div>
              <h3 className="text-xl font-bold text-white mb-2">Guaranteed Savings</h3>
              <p className="text-gray-300 text-sm">
                Lower bills guaranteed
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-6">
            LED Lighting Upgrades Across Watford
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Professional LED installation throughout Watford, Rickmansworth, and surrounding areas
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Watford', 'Rickmansworth', 'Bushey', 'Croxley Green', 'Abbots Langley', 'Kings Langley', 'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Borehamwood', 'Radlett'].map((area) => (
              <span
                key={area}
                className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-5 py-2.5 rounded-full font-semibold text-sm shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Switch to LED in Watford?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Contact us today for a free LED lighting assessment and quote. Start saving money on your electricity bills with professional LED upgrades.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call 020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}